import React from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { ProgressBar } from '../ProgressBar';

/**
 * Generic component to display analytics breakdown data
 * 
 * @param {Object} props
 * @param {Object} props.data - The analytics data object (e.g., racesByPriority, candidatesByContested)
 * @param {string} props.title - Title for the section
 * @param {Function} props.labelFormatter - Optional function to format labels (e.g., capitalize, add spaces)
 * @param {Array} props.expectedKeys - Array of expected keys to always display, even if data is missing
 */
const AnalyticsBreakdown = ({ data, title, labelFormatter = (key) => key, expectedKeys = null }) => {
  // Determine which keys to display
  let keysToDisplay = [];
  
  if (expectedKeys && Array.isArray(expectedKeys)) {
    // Use expected keys if provided
    keysToDisplay = expectedKeys;
  } else if (data && typeof data === 'object') {
    // Otherwise, use keys from data
    keysToDisplay = Object.keys(data);
  }

  if (keysToDisplay.length === 0) {
    return null;
  }

  return (
    <Section>
      {title && (
        <SectionTitle variant="body1" color="textPrimary" style={{ fontWeight: 'bold', marginBottom: '4px' }}>
          {title}
        </SectionTitle>
      )}
      <ProgressItems>
        {keysToDisplay.map((key) => {
          const value = data && data[key];
          const hasData = value && typeof value === 'object' && 
                         typeof value.total === 'number' && 
                         typeof value.completed === 'number';
          
          const total = hasData ? value.total : 0;
          const completed = hasData ? value.completed : 0;
          const percentage = total === 0 ? 0 : Math.round((completed / total) * 100);
          const formattedLabel = labelFormatter(key);

          return (
            <ProgressItem key={key}>
              <Label variant="body2" style={{ fontWeight: '500', marginBottom: '0px' }}>
                {formattedLabel}
              </Label>
              <ProgressBar 
                percentage={percentage} 
                height={8} 
                backgroundColor="#E0E0E0"
              />
              <ProgressText variant="body2" style={{ marginTop: '0px', color: '#666' }}>
                {hasData 
                  ? `${completed} out of ${total} complete (${percentage}%)`
                  : '-- out of -- complete'
                }
              </ProgressText>
            </ProgressItem>
          );
        })}
      </ProgressItems>
    </Section>
  );
};

/**
 * Component to display multiple analytics breakdowns in a popover
 * 
 * @param {Object} props
 * @param {Object} props.analyticsData - Object containing all analytics breakdowns
 * @param {string} props.type - Type of analytics ('races' or 'candidates')
 */
export const AnalyticsPopoverContent = ({ analyticsData, type = 'races' }) => {
  const typeLabel = type === 'races' ? 'Races' : 'Candidates';
  const typeCapitalized = type === 'races' ? 'Races' : 'Candidates';

  const overallCompleted = (analyticsData?.[`num${typeCapitalized}Completed`] ?? 0)
  const overallTotal = (analyticsData?.[`num${typeCapitalized}`] ?? 0)
  const overallProgress = overallTotal === 0 ? 0 : Math.round((overallCompleted / overallTotal) * 100);

  // Format label for priority levels
  const priorityLabelFormatter = (key) => {
    return key.charAt(0).toUpperCase() + key.slice(1) + ' priority';
  };

  // Format label for contested status
  const contestedLabelFormatter = (key) => {
    return key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Format label for combo (contested_high, etc.)
  const comboLabelFormatter = (key) => {
    const [contested, priority] = key.split('_');
    const contestedLabel = contested.charAt(0).toUpperCase() + contested.slice(1);
    const priorityLabel = priority.charAt(0).toUpperCase() + priority.slice(1);
    return `${contestedLabel} - ${priorityLabel} priority`;
  };

  // Define expected keys for each section
  const priorityKeys = ['high', 'medium', 'low'];
  const contestedKeys = ['contested', 'uncontested'];

  return (
    <Container>
      <Typography variant="body1" color="textPrimary" style={{ marginBottom: '12px' }}>
        {overallCompleted} out of {overallTotal} {type} complete ({overallProgress}%)
      </Typography>
      <AnalyticsBreakdown
        data={analyticsData?.[`${type}ByPriority`]}
        title="By Priority Level"
        labelFormatter={priorityLabelFormatter}
        expectedKeys={priorityKeys}
      />

      <AnalyticsBreakdown
        data={analyticsData?.[`${type}ByContested`]}
        title="By Contested Status"
        labelFormatter={contestedLabelFormatter}
        expectedKeys={contestedKeys}
      />
    </Container>
  );
};

const Container = styled.div`
  padding: 16px;
  min-width: 300px;
  max-width: 400px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled(Typography)`
  color: #333;
`;

const ProgressItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled(Typography)`
  color: #333;
`;

const ProgressText = styled(Typography)`
  font-size: 12px;
`;

const Title = styled(Typography)`
  color: #333;
`;

export default AnalyticsBreakdown;
